/**
 * StudentGorusmelerTab.jsx
 * Öğrenciye ait görüşmeleri kronolojik listeler + yeni görüşme ekleme formu.
 * Lead dönemi görüşmeleri `kaynak: "lead"` etiketiyle işaretlenir.
 *
 * Endpoint:
 *   GET  /admin/students/{id}/gorusmeler?page=&page_size=&include_lead=true
 *   POST /admin/students/{id}/gorusmeler
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Phone, MessageSquare, Users, Mail, MessageCircle,
  ChevronDown, Send,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { trNowLocalInput } from '../../utils/trDateTime';
import { EmptyState, StatusBadge } from '../../components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_SIZE = 15;

const KANAL_ICON = {
  telefon: Phone,
  whatsapp: MessageSquare,
  yuz_yuze: Users,
  sms: Mail,
  email: Mail,
};

const INPUT_CLS =
  'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none';

function KanalIcon({ kanal }) {
  const Icon = KANAL_ICON[kanal] || MessageCircle;
  return <Icon size={14} className="inline-block flex-shrink-0 text-[var(--text-muted)]" />;
}

function kanalLabel(kanal, t) {
  const map = {
    telefon: t('admin.studentHub.gorusme_kanal_telefon'),
    whatsapp: t('admin.studentHub.gorusme_kanal_whatsapp'),
    yuz_yuze: t('admin.studentHub.gorusme_kanal_yuz_yuze'),
    sms: t('admin.studentHub.gorusme_kanal_sms'),
    email: t('admin.studentHub.gorusme_kanal_email'),
  };
  return map[kanal] || kanal || '-';
}

const SMS_PURPOSE_KEYS = {
  otp: 'admin.studentHub.sms_purpose_otp',
  credentials: 'admin.studentHub.sms_purpose_credentials',
  info: 'admin.studentHub.sms_purpose_info',
  reminder: 'admin.studentHub.sms_purpose_reminder',
  campaign: 'admin.studentHub.sms_purpose_campaign',
  lead: 'admin.studentHub.sms_purpose_lead',
  test: 'admin.studentHub.sms_purpose_test',
};

function smsPurposeLabel(purpose, t) {
  const key = SMS_PURPOSE_KEYS[purpose];
  return key ? t(key) : (purpose || '-');
}

// Backend status: sent | error | skipped | pending. 'error' -> failed rozeti.
const SMS_STATUS_MAP = {
  sent: { key: 'admin.studentHub.sms_status_sent', variant: 'success' },
  error: { key: 'admin.studentHub.sms_status_failed', variant: 'danger' },
  failed: { key: 'admin.studentHub.sms_status_failed', variant: 'danger' },
  skipped: { key: 'admin.studentHub.sms_status_skipped', variant: 'neutral' },
  pending: { key: 'admin.studentHub.sms_status_pending', variant: 'warning' },
};

function smsStatusInfo(status, t) {
  const m = SMS_STATUS_MAP[status] || { key: null, variant: 'neutral' };
  return { label: m.key ? t(m.key) : (status || '-'), variant: m.variant };
}

function fmtDate(d, lang) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-GB', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return d;
  }
}

export default function StudentGorusmelerTab({ userId }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const [gorusmeler, setGorusmeler] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Gönderilen SMS geçmişi (message_log) — görüşmelerden bağımsız ayrı bölüm
  const [smsHistory, setSmsHistory] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);

  // Form state
  const [kanal, setKanal] = useState('telefon');
  const [konu, setKonu] = useState('');
  const [not, setNot] = useState('');
  const [tarih, setTarih] = useState(() => trNowLocalInput());
  const [saving, setSaving] = useState(false);

  const fetchGorusmeler = useCallback(async (pageNum = 1, append = false) => {
    if (!userId) return;
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/students/${userId}/gorusmeler`,
        {
          params: { page: pageNum, page_size: PAGE_SIZE, include_lead: true },
          headers: authHeaders(),
        }
      );
      const items = r.data.gorusmeler || [];
      setGorusmeler((prev) => (append ? [...prev, ...items] : items));
      const total = r.data.total || 0;
      setHasMore(pageNum * PAGE_SIZE < total);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, lang]);

  const fetchSmsHistory = useCallback(async () => {
    if (!userId) return;
    setSmsLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/students/${userId}/sms-history`,
        { params: { limit: 50 }, headers: authHeaders() }
      );
      setSmsHistory(r.data.items || []);
    } catch {
      // SMS geçmişi ikincil bilgi; görüşme akışını bozmamak için sessiz geç
      setSmsHistory([]);
    } finally {
      setSmsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    setPage(1);
    setGorusmeler([]);
    fetchGorusmeler(1, false);
  }, [fetchGorusmeler]);

  useEffect(() => {
    fetchSmsHistory();
  }, [fetchSmsHistory]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchGorusmeler(next, true);
  };

  const handleAddGorusme = async () => {
    if (!konu.trim()) {
      toast.error(lang === 'tr' ? 'Konu zorunludur' : 'Subject is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/students/${userId}/gorusmeler`,
        { kanal, konu: konu.trim(), not: not.trim(), tarih },
        { headers: authHeaders() }
      );
      toast.success(t('admin.studentHub.gorusme_added'));
      setKonu('');
      setNot('');
      setTarih(trNowLocalInput());
      // Refresh list from beginning
      setPage(1);
      fetchGorusmeler(1, false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* ── Yeni görüşme formu ─────────────────────────────────────────────── */}
      <div className="bg-gray-50 rounded-xl p-4 border-2 border-[var(--border-light)]">
        <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider mb-3">
          {t('admin.studentHub.gorusme_add_title')}
        </p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">
              {t('admin.studentHub.gorusme_kanal')}
            </label>
            <select
              value={kanal}
              onChange={(e) => setKanal(e.target.value)}
              className={INPUT_CLS}
              disabled={saving}
            >
              {['telefon', 'whatsapp', 'yuz_yuze', 'sms', 'email'].map((k) => (
                <option key={k} value={k}>
                  {kanalLabel(k, t)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">
              {t('admin.studentHub.gorusme_tarih')}
            </label>
            <input
              type="datetime-local"
              value={tarih}
              onChange={(e) => setTarih(e.target.value)}
              className={INPUT_CLS}
              disabled={saving}
            />
          </div>
        </div>
        <div className="mb-3">
          <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">
            {t('admin.studentHub.gorusme_konu')}
          </label>
          <input
            type="text"
            value={konu}
            onChange={(e) => setKonu(e.target.value)}
            className={INPUT_CLS}
            disabled={saving}
            placeholder={lang === 'tr' ? 'Görüşme konusu...' : 'Interview subject...'}
          />
        </div>
        <div className="mb-3">
          <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">
            {t('admin.studentHub.gorusme_not')}
          </label>
          <textarea
            value={not}
            onChange={(e) => setNot(e.target.value)}
            rows={2}
            className={`${INPUT_CLS} resize-none`}
            disabled={saving}
            placeholder={lang === 'tr' ? 'Ek not (isteğe bağlı)...' : 'Additional note (optional)...'}
          />
        </div>
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={handleAddGorusme}
          disabled={saving || !konu.trim()}
          className="w-full py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-b-4 border-[var(--brand-primary-shadow)] disabled:opacity-40"
          data-testid="gorusme-add-btn"
        >
          {saving ? t('admin.studentHub.gorusme_adding') : t('admin.studentHub.gorusme_btn_add')}
        </motion.button>
      </div>

      {/* ── Görüşme listesi ────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-[3px] border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : gorusmeler.length === 0 ? (
        <EmptyState
          title={t('admin.studentHub.gorusme_empty')}
          icon={<MessageCircle size={40} strokeWidth={1.5} />}
        />
      ) : (
        <div className="space-y-2">
          {gorusmeler.map((g, i) => {
            const isLead = g.kaynak === 'lead';
            return (
              <div
                key={g.gorusme_id || g._id || i}
                className={`rounded-xl border-2 p-3 ${
                  isLead
                    ? 'border-amber-200 bg-amber-50/50'
                    : 'border-[var(--border-light)] bg-white'
                }`}
                data-testid={`gorusme-row-${i}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <KanalIcon kanal={g.kanal} />
                    <span className="text-xs font-bold truncate">
                      {g.personel_adi || '-'}
                    </span>
                    {isLead && (
                      <StatusBadge
                        variant="warning"
                        label={t('admin.studentHub.gorusme_lead_badge')}
                        className="text-[9px] flex-shrink-0"
                      />
                    )}
                  </div>
                  <span className="text-[10px] text-[var(--text-muted)] flex-shrink-0">
                    {fmtDate(g.tarih || g.created_at, lang)}
                  </span>
                </div>
                {g.konu && (
                  <p className="text-sm font-semibold text-[var(--text-main)] mb-0.5">
                    {g.konu}
                  </p>
                )}
                {g.not && (
                  <p className="text-xs text-[var(--text-muted)]">{g.not}</p>
                )}
                <div className="flex items-center gap-1 mt-1">
                  <span className="flex items-center gap-0.5 text-[10px] text-[var(--text-muted)] bg-gray-100 rounded-md px-1.5 py-0.5">
                    <KanalIcon kanal={g.kanal} />
                    {kanalLabel(g.kanal, t)}
                  </span>
                </div>
              </div>
            );
          })}

          {hasMore && (
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-xs font-bold text-[var(--text-muted)] hover:bg-gray-50 disabled:opacity-40"
              data-testid="gorusme-load-more"
            >
              {loadingMore ? (
                <div className="w-4 h-4 border-2 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <ChevronDown size={14} />
                  {t('admin.studentHub.gorusme_load_more')}
                </>
              )}
            </motion.button>
          )}
        </div>
      )}

      {/* ── Gönderilen SMS'ler (message_log) — ayrı bölüm ──────────────────── */}
      <div className="pt-2">
        <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Send size={12} className="text-[var(--text-muted)]" />
          {t('admin.studentHub.sms_history_title')}
        </p>
        {smsLoading ? (
          <div className="flex justify-center py-6">
            <div className="w-5 h-5 border-[3px] border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : smsHistory.length === 0 ? (
          <EmptyState
            title={t('admin.studentHub.sms_history_empty')}
            icon={<Send size={32} strokeWidth={1.5} />}
          />
        ) : (
          <div className="space-y-2">
            {smsHistory.map((m, i) => {
              const st = smsStatusInfo(m.status, t);
              return (
                <div
                  key={m.log_id || i}
                  className="rounded-xl border-2 border-[var(--border-light)] bg-white p-3"
                  data-testid={`sms-row-${i}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Mail size={14} className="flex-shrink-0 text-[var(--text-muted)]" />
                      <span className="text-xs font-bold truncate">
                        {smsPurposeLabel(m.purpose, t)}
                      </span>
                      <StatusBadge
                        variant={st.variant}
                        label={st.label}
                        className="text-[9px] flex-shrink-0"
                      />
                    </div>
                    <span className="text-[10px] text-[var(--text-muted)] flex-shrink-0">
                      {fmtDate(m.created_at, lang)}
                    </span>
                  </div>
                  {m.body_preview && (
                    <p className="text-xs text-[var(--text-muted)] whitespace-pre-line break-words">
                      {m.body_preview}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5">
                    {m.to_masked && (
                      <span className="text-[10px] text-[var(--text-muted)] bg-gray-100 rounded-md px-1.5 py-0.5">
                        {m.to_masked}
                      </span>
                    )}
                    {m.segment_count > 1 && (
                      <span className="text-[10px] text-[var(--text-muted)] bg-gray-100 rounded-md px-1.5 py-0.5">
                        {m.segment_count} SMS
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
